import { useState, useEffect, useRef, useCallback } from 'react';
import './ColorPicker.css';

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  label?: string;
}

// Validate hex color format
function isValidHex(hex: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(hex);
}

// Normalize hex input (add # if missing, uppercase)
function normalizeHex(value: string): string {
  let hex = value.trim();
  if (!hex.startsWith('#')) {
    hex = '#' + hex;
  }
  return hex.toUpperCase();
}

export function ColorPicker({ color, onChange, label }: ColorPickerProps) {
  const [hexInput, setHexInput] = useState(color);
  // Local color state for responsive swatch updates during picker drag
  const [localColor, setLocalColor] = useState(color);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync states when color prop changes externally
  useEffect(() => {
    setHexInput(color);
    setLocalColor(color);
  }, [color]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Debounced onChange to avoid hammering parent with updates
  const debouncedOnChange = useCallback((newColor: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      onChange(newColor);
    }, 16); // ~1 frame, keeps it responsive but batches rapid updates
  }, [onChange]);

  const handleHexChange = (value: string) => {
    setHexInput(value);
    const normalized = normalizeHex(value);
    if (isValidHex(normalized)) {
      setLocalColor(normalized);
      onChange(normalized);
    }
  };

  const handleHexBlur = () => {
    const normalized = normalizeHex(hexInput);
    if (isValidHex(normalized)) {
      setHexInput(normalized);
      setLocalColor(normalized);
      onChange(normalized);
    } else {
      // Revert to current valid color
      setHexInput(color);
      setLocalColor(color);
    }
  };

  const handleColorPickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value.toUpperCase();
    setHexInput(newColor);
    setLocalColor(newColor);
    debouncedOnChange(newColor);
  };

  const handleSwatchClick = () => {
    // Directly trigger the native color picker
    colorInputRef.current?.click();
  };

  return (
    <div className="color-picker">
      {label && <label className="color-picker-label">{label}</label>}
      <div className="color-picker-controls">
        <button
          type="button"
          className="color-swatch"
          style={{ backgroundColor: localColor }}
          onClick={handleSwatchClick}
          title="Click to open color picker"
        />
        <input
          type="text"
          className="hex-input"
          value={hexInput}
          onChange={(e) => handleHexChange(e.target.value)}
          onBlur={handleHexBlur}
          placeholder="#000000"
          maxLength={7}
        />
        {/* Hidden native color picker, triggered by swatch click */}
        <input
          ref={colorInputRef}
          type="color"
          value={localColor}
          onChange={handleColorPickerChange}
          className="native-color-picker"
        />
      </div>
    </div>
  );
}
